/**
 * Secure chats (Oct 2026): the numbers, the copy and the pure logic behind the lock
 * sheet, the vault bar and the padlock in the inbox header.
 * Handoff: ~/Downloads/design_handoff_secure_messages/README.md (not in repo).
 *
 * Nothing in here touches the DOM or the network. The lock itself lives on the server
 * (`/messages/secure/*`); this file knows how to draw a pattern, read a status and
 * word an error.
 */

export const SECURE = {
  /** Error on black — `#EF4444` has no contrast on the sheet. */
  error: '#FF4D6D',
  track: '#1F1F1F',
  keyBorder: 'rgba(255,255,255,.28)',
  muted50: 'rgba(255,255,255,.5)',
  muted55: 'rgba(255,255,255,.55)',
  muted65: 'rgba(255,255,255,.65)',
  muted75: 'rgba(255,255,255,.75)',
  /** The DS Outside-theme overlay, laid over the sheet. */
  scanlines: 'repeating-linear-gradient(0deg, rgba(255,255,255,.05) 0 1px, transparent 1px 3px)',
};

export const PIN_LENGTH = 4;
export const PATTERN_MIN = 4;
export const WINDOW_CHOICES = [1, 5, 15];
export const DEFAULT_WINDOW_MIN = 5;
export const ATTEMPTS_PER_LOCKOUT = 5;
/** The lock sheet waits this long after the last digit so the fourth dot is seen filled. */
export const PIN_SUBMIT_DELAY_MS = 160;
export const PATTERN_SUBMIT_DELAY_MS = 120;
export const ERROR_FLASH_MS = 700;
export const TOAST_MS = 2400;

/** The keypad, row-major: 1–9, a blank, 0, DEL. */
export const PIN_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'DEL'];

// ── The pattern pad ───────────────────────────────────────────────────────────────────

export const PAD_SIZE = 240;
export const PAD_HIT_RADIUS = 30;
const PAD_CENTRES = [40, 120, 200];

/** Centre of dot i (0–8, row-major) in pad coordinates. */
export function dotCentre(i) {
  return { x: PAD_CENTRES[i % 3], y: PAD_CENTRES[Math.floor(i / 3)] };
}

/** Which dot a point (pad coordinates) lands on, or null. */
export function hitDot(p) {
  for (let i = 0; i < 9; i++) {
    const c = dotCentre(i);
    if (Math.hypot(p.x - c.x, p.y - c.y) < PAD_HIT_RADIUS) return i;
  }
  return null;
}

/** A pointer event's position in pad coordinates, however big the pad is drawn. */
export function padPoint(clientX, clientY, rect) {
  const k = rect.width ? PAD_SIZE / rect.width : 1;
  return { x: (clientX - rect.left) * k, y: (clientY - rect.top) * k };
}

/**
 * Add a dot to the path. Android's rule: a jump over an unused dot (0 → 2 passes 1,
 * 0 → 8 passes 4) takes the middle dot first. A dot already in the path is ignored.
 */
export function extendPattern(seq, next) {
  if (next == null || seq.includes(next)) return seq;
  const out = seq.slice();
  const last = out[out.length - 1];
  if (last != null) {
    const ra = Math.floor(last / 3), ca = last % 3, rb = Math.floor(next / 3), cb = next % 3;
    if ((ra + rb) % 2 === 0 && (ca + cb) % 2 === 0) {
      const mid = ((ra + rb) / 2) * 3 + (ca + cb) / 2;
      if (!out.includes(mid)) out.push(mid);
    }
  }
  out.push(next);
  return out;
}

/** The ordered dot indices as the code the server stores: "0124". */
export function encodePattern(seq) {
  return seq.join('');
}

/**
 * The line segments to draw: dot to dot, plus a trailing one to the pointer while the
 * finger is down. Each is `{ x, y, len, ang }` with the origin at the segment's start.
 */
export function patternLines(seq, pointer = null) {
  const pts = seq.map(dotCentre);
  if (pointer && pts.length) pts.push(pointer);
  const lines = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    lines.push({ x: a.x, y: a.y, len: Math.hypot(b.x - a.x, b.y - a.y), ang: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI });
  }
  return lines;
}

// ── Copy ──────────────────────────────────────────────────────────────────────────────

export const SETUP_NOTE = 'Secure chats are hidden from your threads. Notifications still arrive, without the sender or the message.';
export const FORGOT_NOTE = 'Enter your account password to set a new lock. Your secure chats stay where they are.';
export const EMPTY_BODY = 'Open the ⋯ menu on any thread and choose Move to secure. It will only show here, behind your PIN or pattern.';

/**
 * The sheet's kicker, title and subtitle for one modal state.
 *   kind  'setup' | 'unlock'
 *   step  'enter' | 'confirm' | 'forgot'
 *   type  'pin' | 'pattern'
 *   change  true when a lock already exists (SET A LOCK → CHANGE LOCK)
 */
export function sheetCopy({ kind, step, type, change = false, confirm = false }) {
  const pin = type === 'pin';
  if (step === 'forgot') return { kicker: 'SECURE CHATS · RESET', title: 'RESET LOCK', sub: 'CONFIRM IT IS YOU' };
  if (kind === 'unlock' && confirm) return { kicker: 'SECURE CHATS · CONFIRM', title: 'CONFIRM IT IS YOU', sub: pin ? 'ENTER YOUR PIN' : 'DRAW YOUR PATTERN' };
  if (kind === 'unlock') return { kicker: 'SECURE CHATS · LOCKED', title: 'SECURE CHATS', sub: pin ? 'ENTER YOUR PIN' : 'DRAW YOUR PATTERN' };
  if (step === 'confirm') return { kicker: 'SETUP · STEP 2 OF 2', title: 'CONFIRM', sub: pin ? 'ENTER THE PIN AGAIN' : 'DRAW THE PATTERN AGAIN' };
  return { kicker: 'SETUP · STEP 1 OF 2', title: change ? 'CHANGE LOCK' : 'SET A LOCK', sub: pin ? 'CHOOSE A 4-DIGIT PIN' : 'DRAW A PATTERN · 4+ DOTS' };
}

export function wrongCodeError(type, triesLeft) {
  return `${type === 'pin' ? 'WRONG PIN' : 'WRONG PATTERN'} · ${triesLeft} ${triesLeft === 1 ? 'TRY' : 'TRIES'} LEFT`;
}

/** "0:27" — the seconds left on a lockout, or on the unlock window. */
export function mmss(ms) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function lockoutError(lockedUntilMs, now = Date.now()) {
  return `TOO MANY ATTEMPTS · TRY AGAIN IN ${mmss(lockedUntilMs - now)}`;
}

export const LOCK_TYPE_LABEL = { pin: 'PIN', pattern: 'PATTERN' };

// ── The session ───────────────────────────────────────────────────────────────────────

/**
 * Whether an unlock is still inside the window. `unlockedAt` is a ms timestamp or null;
 * the server keeps the authoritative copy, this one drives the countdown and the
 * auto-lock without a round trip.
 */
export function isUnlocked(unlockedAt, windowMinutes, now = Date.now()) {
  return !!unlockedAt && unlockedAt + windowMinutes * 60000 > now;
}

export function msUntilLock(unlockedAt, windowMinutes, now = Date.now()) {
  if (!unlockedAt) return 0;
  return Math.max(0, unlockedAt + windowMinutes * 60000 - now);
}

/**
 * Read the server's status into the client's session shape. Its clock, not ours: the
 * window is re-based on `serverNow` so a skewed laptop doesn't lock early or late.
 */
export function sessionFromStatus(status, now = Date.now()) {
  if (!status) return { hasLock: false, type: null, windowMinutes: DEFAULT_WINDOW_MIN, unlockedAt: null, lockedUntil: null, triesLeft: ATTEMPTS_PER_LOCKOUT, hasPassword: false, passkeysHere: 0 };
  const skew = status.serverNow ? now - new Date(status.serverNow).getTime() : 0;
  const shift = (iso) => (iso ? new Date(iso).getTime() + skew : null);
  return {
    hasLock: !!status.hasLock,
    type: status.type || null,
    windowMinutes: status.windowMinutes || DEFAULT_WINDOW_MIN,
    unlockedAt: shift(status.unlockedAt),
    lockedUntil: shift(status.lockedUntil),
    triesLeft: status.triesLeft ?? ATTEMPTS_PER_LOCKOUT,
    hasPassword: !!status.hasPassword,
    // Only the GET says how many passkeys this site holds; other answers leave it unknown.
    passkeysHere: status.passkeysHere ?? null,
  };
}

/** Which thread opens on entering the vault: the one asked for, else the open secure one, else the first. */
export function vaultEntryThread(threads, activeId, pendingThreadId = null) {
  if (pendingThreadId && threads.some((t) => t.id === pendingThreadId && t.isSecure)) return pendingThreadId;
  const cur = threads.find((t) => t.id === activeId && t.isSecure);
  if (cur) return cur.id;
  return threads.find((t) => t.isSecure)?.id || null;
}

/** The first thread that is NOT secure — where the pane lands when the vault shuts. */
export function firstRegularThread(threads) {
  return threads.find((t) => !t.isSecure)?.id || null;
}

/** A notification about a secure thread: the generic title, no actor. */
export function isSecureNotification(n) {
  return !!n && (n.type === 'MessageReceived' || n.type === 'MessageMention' || n.type === 'MessageReaction')
    && !n.actor && /^you received /i.test(n.title || '');
}
